import axiosInstance from '../config/axios.js';
import { downloadBlob, filenameFromContentDisposition } from '../utils/downloadBlob.js';

const BASE = '/company-settlements';

/** Comma-separated hub ids — Flask getlist + _parse_hub_ids both accept this. */
export const settlementHubQueryParams = (params = {}) => {
  const next = { ...params };
  const ids = next.location_hub_ids ?? next.hub_ids;
  delete next.location_hub_ids;
  delete next.hub_ids;
  if (Array.isArray(ids) && ids.length) {
    const joined = ids.map(String).filter(Boolean).join(',');
    if (joined) {
      next.location_hub_ids = joined;
      next.hub_ids = joined;
    }
  } else if (typeof ids === 'string' && ids.trim()) {
    next.location_hub_ids = ids.trim();
    next.hub_ids = ids.trim();
  }
  return next;
};

const companySettlementService = {
  getSummary: async (params = {}) => {
    const response = await axiosInstance.get(`${BASE}/summary`, {
      params: settlementHubQueryParams(params),
    });
    return response.data;
  },

  listBatches: async (params = {}) => {
    const response = await axiosInstance.get(`${BASE}/batches`, {
      params: settlementHubQueryParams(params),
    });
    return response.data;
  },

  getBatch: async (batchId) => {
    const response = await axiosInstance.get(`${BASE}/batches/${encodeURIComponent(batchId)}`);
    return response.data;
  },

  submitBatch: async (formData) => {
    const response = await axiosInstance.post(`${BASE}/batches`, formData, {
      headers: { 'Content-Type': undefined },
    });
    return response.data;
  },

  approveBatch: async (batchId, payload = {}) => {
    const response = await axiosInstance.post(
      `${BASE}/batches/${encodeURIComponent(batchId)}/approve`,
      payload,
    );
    return response.data;
  },

  listBranchAgents: async (locationHubId) => {
    const response = await axiosInstance.get(`${BASE}/branch-agents`, {
      params: { location_hub_id: locationHubId },
    });
    return response.data;
  },

  recordReceived: async (formData) => {
    const response = await axiosInstance.post(`${BASE}/record-received`, formData, {
      headers: { 'Content-Type': undefined },
    });
    return response.data;
  },

  payAgent: async (formData) => {
    const response = await axiosInstance.post(`${BASE}/pay-agent`, formData, {
      headers: { 'Content-Type': undefined },
    });
    return response.data;
  },

  rejectBatch: async (batchId, payload = {}) => {
    const response = await axiosInstance.post(
      `${BASE}/batches/${encodeURIComponent(batchId)}/reject`,
      payload,
    );
    return response.data;
  },

  downloadExcel: async (params = {}) => {
    const response = await axiosInstance.get(`${BASE}/statement.xlsx`, {
      params: settlementHubQueryParams(params),
      responseType: 'blob',
    });
    const name = filenameFromContentDisposition(
      response.headers?.['content-disposition'],
      'commission_settlement.xlsx',
    );
    await downloadBlob(
      response.data,
      name,
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    );
  },

  downloadPdf: async (params = {}) => {
    const response = await axiosInstance.get(`${BASE}/statement.pdf`, {
      params: settlementHubQueryParams(params),
      responseType: 'blob',
    });
    const name = filenameFromContentDisposition(
      response.headers?.['content-disposition'],
      'commission_settlement.pdf',
    );
    await downloadBlob(response.data, name, 'application/pdf');
  },
};

export default companySettlementService;
